import { useEffect, useState, useCallback } from 'react';
import { useUIStore } from '../../store';

export type InsuranceBffState = 'idle' | 'loading' | 'passed' | 'passed-no-insurance' | 'failed';

interface UseInsuranceEsiArgs {
  enabled: boolean; // experiments['se-20']
  isInsuranceAvailable: boolean;
  productName: string;
}

interface UseInsuranceEsiResult {
  dispatchAtbIntercept: () => void;
  shouldInterceptAtb: (sku: string) => boolean;
}

export function useInsuranceEsi({
  enabled,
  isInsuranceAvailable,
  productName,
}: UseInsuranceEsiArgs): UseInsuranceEsiResult {
  const [hasInsuranceBeenSelected, setHasInsuranceBeenSelected] = useState(false);

  const skuSelected = useUIStore((s: any) => s.skuSelected);
  const isSkuReady = Boolean(skuSelected);

  /**
   * Reset only when experiment is disabled
   */
  useEffect(() => {
    if (!enabled) {
      setHasInsuranceBeenSelected(false);
    }
  }, [enabled]);

  useEffect(() => {
    const handleGoToBasket = () => {
      window.location.href = '/basket';
    };

    window.addEventListener('insurance-fe:go-to-basket', handleGoToBasket);

    return () => {
      window.removeEventListener('insurance-fe:go-to-basket', handleGoToBasket);
    };
  }, []);

  /**
   * Insurance selection event (from ESI)
   */
  useEffect(() => {
    const onInsuranceUpdated = (e) => {
      setHasInsuranceBeenSelected(!!e.detail?.selectedInsurance);
    };

    window.addEventListener('insurance-fe:insurance-updated', onInsuranceUpdated);
    return () => window.removeEventListener('insurance-fe:insurance-updated', onInsuranceUpdated);
  }, []);

  /**
   * Should we intercept Add To Basket?
   */
  // const insuranceBffState = useUIStore((s) => s.insuranceBffState);
  const shouldInterceptAtb = useCallback(
    (sku) => {
      const { insuranceBffState, noInsuranceSku } = useUIStore.getState();
      if (!enabled || !isInsuranceAvailable) return false;
      if (noInsuranceSku.includes(sku)) return false;
      if (insuranceBffState === 'failed' || insuranceBffState === 'passed-no-insurance') {
        return false;
      }

      return insuranceBffState === 'passed' && isSkuReady && !hasInsuranceBeenSelected;
    },
    [enabled, isInsuranceAvailable, isSkuReady, hasInsuranceBeenSelected],
  );

  const dispatchAtbIntercept = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent('product-fe:atb-clicked', {
        detail: { productName },
      }),
    );
  }, [productName]);

  return {
    shouldInterceptAtb,
    dispatchAtbIntercept,
  };
}
